import React, { useCallback, useEffect, useState } from "react";
import TableContainer from "Common/TableContainer";
import { Loader } from "../../Common/Loader";
import { Pencil, Trash2, Plus } from "lucide-react";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Common } from "helpers/common";
import moment from "moment";
import { AddTeacher } from "./AddTeacher";
import { getTeacherList, deleteTeacher } from "helpers/backend_helper";

function TeacherList() {
  const [teachers, setTeachers] = useState<any[]>([]);
  const [filteredTeachers, setFilteredTeachers] = useState<any[]>([]);
  const [eventData, setEventData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [show, setShow] = useState<boolean>(false);
  const [teacherData, setTeacherData] = useState<any>({});
  const uiHelper = new Common();

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  useEffect(() => {
    document.body.classList.remove('overflow-hidden');
    loadTeachers(false);
  }, []);

  async function loadTeachers(reload: boolean) {
    try {
      const response = await getTeacherList();
      console.log("Full teacher response:", response);
      setTeachers(response.data);
      setFilteredTeachers(response.data);
    } catch (error) {
      console.error("Error loading teachers:", error);
    } finally {
      setIsLoading(false);
    }
  }

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell.teacherId) {
      setEventData(cell);
    }
  };

  const handleDelete = async () => {
    if (eventData) {
      try {
        await deleteTeacher(eventData);
        setDeleteModal(false);
        uiHelper.showSuccessMessage("Teacher has been deleted successfully.");

        // remove the item and update list
        setTeachers((prev) =>
          prev.filter((item) => item.teacherId !== eventData.teacherId)
        );
        setFilteredTeachers((prev) =>
          prev.filter((item) => item.teacherId !== eventData.teacherId)
        );
      } catch (error) {
        console.error("Error deleting teacher:", error);
        uiHelper.showErrorMessage("Error deleting teacher");
      }
    }
  };

  const filterSearchData = (e: any) => {
    const searchText = e.target.value;
    setSearchText(searchText);

    if (searchText.length > 0) {
      const filteredData = teachers.filter(
        (item) =>
          item.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.email.toLowerCase().includes(searchText.toLowerCase()) ||
          item.phone.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredTeachers(filteredData);
    } else {
      setFilteredTeachers(teachers);
    }
  };

  const handleAddTeacher = () => {
    setTeacherData({});
    toggle();
  };

  const handleEdit = (data: any) => {
    setTeacherData(data);
    toggle();
  };

  const onSave = (result: any) => {
    console.log("Save result:", result);
    loadTeachers(true);
    toggle();
  };

  const toggle = useCallback(() => {
    setShow(!show);
    if (show) {
      setTeacherData(null);
    }
  }, [show]);

  const columns = [
    {
      header: "First Name",
      accessorKey: "firstName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Last Name",
      accessorKey: "lastName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Phone",
      accessorKey: "phone",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "City",
      accessorKey: "city",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "State",
      accessorKey: "state",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Salary Type",
      accessorKey: "salaryType",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.salaryType === 1 ? "Hourly" : "Monthly"}</span>
      ),
    },
    {
      header: "Rate",
      accessorKey: "rate",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>${row.original.rate?.toFixed(2) || '0.00'}</span>
      ),
    },
    {
      header: "Joined On",
      accessorKey: "joinedOn",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.joinedOn ? moment(row.original.joinedOn).format("MMM DD, YYYY") : ''}</span>
      ),
    },
    {
      header: "Created On",
      accessorKey: "createdOn",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{moment(row.original.createdOn).format("MMM DD, YYYY")}</span>
      ),
    },
    {
      header: "Created By",
      accessorKey: "createdByName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Actions",
      id: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => {
        const teacher = row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 text-blue-500 bg-blue-100 rounded hover:bg-blue-500 hover:text-white transition-colors duration-200"
              onClick={() => handleEdit(teacher)}
            >
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 text-red-500 bg-red-100 rounded hover:bg-red-500 hover:text-white transition-colors duration-200"
              onClick={() => onClickDelete(teacher)}
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12" style={{ marginTop: 15 }}>
        <div className="xl:col-span-12">
          <div className="card" id="teacherTable">

            <div className="flex items-center justify-between border-y border-dashed" style={{ padding: 10 }}>
              <div className="flex items-center gap-2 shrink-0">
                <h6 className="text-15">Filter </h6>
                <input
                  type="text"
                  className="w-full form-input  focus:outline-none focus:border-none focus:ring focus:ring-custom-100"
                  placeholder="Teacher Name, Email, or Phone"
                  value={searchText}
                  onChange={filterSearchData}
                />

                <div className="flex justify-end shrink-0">
                  <button
                    type="button"
                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={handleAddTeacher}
                    style={{ marginLeft: "auto" }}
                  >
                    <Plus className="inline-block size-4" />{" "}
                    <span className="align-middle">Add Teacher</span>
                  </button>
                </div>
              </div>
            </div>


            {isLoading ? (
              <Loader />
            ) : (
              <TableContainer
                columns={columns}
                data={filteredTeachers}
                isPagination={true}
                customPageSize={15}
                divclassName="overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-custom-200 dark:bg-zink-600"
                thclassName="px-2.5 py-1.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                tdclassName="px-2.5 py-1.0 border-y border-slate-200 dark:border-zink-500"
                trclassName="even:bg-custom-50  dark:odd:bg-zink-700 dark:even:bg-zink-600"
                PaginationClassName="flex flex-col items-center gap-4 px-4 mt-3 md:flex-row"
              />
            )}

          </div>
        </div>
      </div>


      {teacherData ? (
        <AddTeacher 
          show={show} 
          onHide={toggle} 
          data={teacherData} 
          onSave={onSave}
        />) : (<></>)}

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
    </React.Fragment>
  );
}

export default TeacherList;

